import { partitionAttributes, presentAttributes, type PresentedAttribute } from "../lib/manifest";
import type { Manifest } from "../lib/types";

export type AttributeTableProps = {
  manifest: Manifest | undefined;
  /** The entry's registry domain key. */
  domain: string | null;
  attributes: Record<string, unknown>;
  /**
   * `rows` puts the label and value on one line, field-guide style.
   * `block` stacks them, for narrow institutional label blocks.
   */
  layout?: "rows" | "block";
  className?: string;
  /** Long-form `text` attributes are rendered as paragraphs below the table. */
  includeProse?: boolean;
  /** Attribute keys to leave out — for one already shown as a headline fact. */
  excludeKeys?: readonly string[];
};

/**
 * Manifest-driven, top to bottom.
 *
 * Order comes from the manifest's `attrs` array, which is deliberate and reads
 * like a field guide. Labels and enum spellings come from the manifest too —
 * nothing here knows what a "leaf type" is, which is why a ninth domain will
 * render correctly the day it ships, with no change to this file.
 *
 * Prominent attributes that have no value still get a row: a blank ruled line,
 * like an unfinished herbarium sheet.
 */
export function AttributeTable({
  manifest,
  domain,
  attributes,
  layout = "rows",
  className,
  includeProse = true,
  excludeKeys,
}: AttributeTableProps) {
  const presented = presentAttributes(manifest, domain, attributes).filter(
    (attribute) => !excludeKeys?.includes(attribute.key),
  );
  const { rows, paragraphs } = partitionAttributes(presented);

  if (rows.length === 0 && paragraphs.length === 0) return null;

  return (
    <div className={className}>
      {rows.length > 0 ? (
        <dl className="divide-y divide-rule border-t border-rule">
          {rows.map((attribute) => (
            <AttributeRow key={attribute.key} attribute={attribute} layout={layout} />
          ))}
        </dl>
      ) : null}

      {includeProse && paragraphs.length > 0 ? (
        <div className="mt-4 space-y-3">
          {paragraphs.map((attribute) => (
            <div key={attribute.key}>
              <p className="field-label">{attribute.label}</p>
              <p className="mt-1 font-prose leading-relaxed text-ink">{attribute.display}</p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AttributeRow({
  attribute,
  layout,
}: {
  attribute: PresentedAttribute;
  layout: "rows" | "block";
}) {
  const isBlank = attribute.display === null;

  if (layout === "block") {
    return (
      <div className="py-1.5">
        <dt className="field-label-tight text-ink-soft">{attribute.label}</dt>
        <dd className="mt-0.5 font-prose text-[0.9375rem] leading-snug text-ink">
          {isBlank ? <BlankLine label={attribute.label} /> : renderValue(attribute)}
        </dd>
      </div>
    );
  }

  return (
    <div className="flex items-baseline gap-3 py-2">
      <dt className="field-label w-[9.5rem] shrink-0 pt-px">{attribute.label}</dt>
      <dd className="min-w-0 flex-1 font-prose text-[0.9375rem] leading-snug text-ink">
        {isBlank ? <BlankLine label={attribute.label} /> : renderValue(attribute)}
      </dd>
    </div>
  );
}

function renderValue(attribute: PresentedAttribute) {
  // A short list reads better as a run than as bullets.
  if (attribute.items && attribute.items.length > 1) {
    return (
      <span>
        {attribute.items.map((item, index) => (
          <span key={item}>
            {index > 0 ? <span className="text-rule">, </span> : null}
            {item}
          </span>
        ))}
      </span>
    );
  }
  return <span>{attribute.display}</span>;
}

/**
 * The unfilled field. Prominent attributes always get their line, so the sheet
 * shows what it doesn't yet know rather than hiding it.
 */
function BlankLine({ label }: { label: string }) {
  return (
    <span className="ruled block w-full max-w-[16rem]" role="presentation">
      <span className="sr-only">{`${label}: not recorded`}</span>
    </span>
  );
}
